import type { Article, ArticleContentBlock, RichTextBlock, RichTextInline } from '@/domain/article'
import { articlePath, absoluteUrl } from '@/lib/url'
import { siteConfig } from '@/config/site'
import { team } from '@/config/team'

export type SeoMeta = {
  title: string
  description: string
  canonicalPath: string
  image?: string
  type?: 'website' | 'article'
  noindex?: boolean
  /** Emit `robots: index, follow, max-image-preview:large, max-snippet:-1` so answer engines can lift the full block. Opt-in per page. */
  fullSnippet?: boolean
  /** ISO date for `article:published_time` (only emitted when type === 'article'). */
  publishedTime?: string
  /** ISO date for `article:modified_time` (only emitted when type === 'article'). */
  modifiedTime?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export const pageTitle = (title: string) => `${title} | ${siteConfig.name}`

const organizationId = `${siteConfig.siteUrl}/#organization`
const websiteId = `${siteConfig.siteUrl}/#website`

export const organizationJsonLd = () => ({
  '@type': 'Organization',
  '@id': organizationId,
  name: siteConfig.name,
  legalName: siteConfig.legal.entityName,
  url: siteConfig.siteUrl,
  description: siteConfig.description,
  address: {
    '@type': 'PostalAddress',
    streetAddress: siteConfig.legal.office.street,
    addressLocality: siteConfig.legal.office.city,
    addressRegion: siteConfig.legal.office.region,
    postalCode: siteConfig.legal.office.postalCode,
    addressCountry: siteConfig.legal.office.country,
  },
  logo: absoluteUrl(siteConfig.logo),
  image: absoluteUrl(siteConfig.ogImage),
  sameAs: siteConfig.sameAs,
  brand: { '@type': 'Brand', name: siteConfig.product.name, alternateName: siteConfig.product.formerName },
})

/** The team as people working at the organisation, in the words the home page uses. */
export const teamJsonLd = () =>
  team.map((person) => ({
    '@type': 'Person',
    '@id': `${siteConfig.siteUrl}/#${person.name.toLowerCase().replace(/[^a-z]+/g, '-')}`,
    name: person.name,
    jobTitle: person.role,
    description: person.bio,
    image: absoluteUrl(person.photo),
    sameAs: [person.linkedin],
    worksFor: { '@id': organizationId },
  }))

export const websiteJsonLd = () => ({
  '@type': 'WebSite',
  '@id': websiteId,
  name: siteConfig.name,
  url: siteConfig.siteUrl,
  publisher: {
    '@id': organizationId,
  },
})

type PageJsonLdOptions = {
  type?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage' | 'Blog' | 'ProfilePage'
  path: string
  name: string
  description: string
  primaryImage?: string
}

export const pageJsonLd = ({
  type = 'WebPage',
  path,
  name,
  description,
  primaryImage = siteConfig.ogImage,
}: PageJsonLdOptions) => ({
  '@type': type,
  '@id': `${absoluteUrl(path)}#webpage`,
  url: absoluteUrl(path),
  name,
  description,
  isPartOf: {
    '@id': websiteId,
  },
  publisher: {
    '@id': organizationId,
  },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: absoluteUrl(primaryImage),
    width: siteConfig.ogImageWidth,
    height: siteConfig.ogImageHeight,
  },
})

export type FaqSchemaItem = {
  question: string
  answer: string
}

export const faqPageJsonLd = (path: string, items: FaqSchemaItem[]) => ({
  '@type': 'FAQPage',
  '@id': `${absoluteUrl(path)}#faq`,
  url: absoluteUrl(path),
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
})

export const breadcrumbJsonLd = (items: Array<{ name: string; path: string }>) => ({
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
})

export const pageSchema = (page: PageJsonLdOptions, extra: Record<string, unknown>[] = []) => ({
  '@context': 'https://schema.org',
  '@graph': [organizationJsonLd(), websiteJsonLd(), pageJsonLd(page), ...extra],
})

const imageObject = (url: string) => ({
  '@type': 'ImageObject',
  url: absoluteUrl(url),
  width: siteConfig.ogImageWidth,
  height: siteConfig.ogImageHeight,
})

const inlineToText = (nodes: RichTextInline[] = []): string =>
  nodes.map((node) => (node.type === 'link' ? inlineToText(node.children) : node.text)).join('')

const blocksToText = (blocks: RichTextBlock[] = []): string =>
  blocks
    .map((block) => {
      switch (block.type) {
        case 'paragraph':
        case 'heading':
        case 'quote':
          return inlineToText(block.children)
        case 'list':
          return block.children.map((item) => inlineToText(item.children)).join(' ')
        default:
          return ''
      }
    })
    .filter(Boolean)
    .join(' ')
    .trim()

type FaqBlock = Extract<ArticleContentBlock, { type: 'faq' }>

/** Pull every `:::faq` question/answer from an article body for FAQPage schema. */
export const articleFaqItems = (article: Article): FaqSchemaItem[] =>
  article.body
    .filter((block): block is FaqBlock => block.type === 'faq')
    .flatMap((block) => block.items)
    .map((item) => ({ question: item.title, answer: blocksToText(item.body) }))
    .filter((item) => item.question && item.answer)

export const articleJsonLd = (article: Article) => {
  const path = articlePath(article.slug)
  const description = article.seo?.description ?? article.excerpt
  const articleNode = {
    '@type': 'BlogPosting',
    '@id': `${absoluteUrl(path)}#article`,
    headline: article.title,
    description,
    datePublished: article.publishedAt,
    dateModified: article.updatedAt ?? article.publishedAt,
    author: {
      '@id': organizationId,
    },
    publisher: {
      '@id': organizationId,
    },
    mainEntityOfPage: `${absoluteUrl(path)}#webpage`,
    image: imageObject(article.seo?.image?.url ?? article.coverImage?.url ?? siteConfig.ogImage),
  }

  const faqItems = articleFaqItems(article)

  return {
    '@context': 'https://schema.org',
    '@graph': [
      organizationJsonLd(),
      websiteJsonLd(),
      pageJsonLd({
        path,
        name: article.seo?.title ?? article.title,
        description,
        primaryImage: article.seo?.image?.url ?? article.coverImage?.url ?? siteConfig.ogImage,
      }),
      breadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name: 'Blog', path: '/blog' },
        { name: article.title, path },
      ]),
      articleNode,
      ...(faqItems.length ? [faqPageJsonLd(path, faqItems)] : []),
    ],
  }
}
